import _ from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import { GlobalReducerState } from 'app/reducers';
import {
  makeSelectCommercialField,
  makeSelectConfigField,
  makeSelectJvpField,
  selectIsFirstPageView,
} from 'Containers/App/selectors';
import {
  makeGetBuilderGoSettings,
  makeGetCoveredHouseholdMemberCount,
  makeGetDataRevisionLocation,
  makeGetSelectedPublicationPlanYear,
  makeSelectDependentsCount,
  makeSelectSurvey,
} from 'Containers/ProfilePage/selectors';
import { RiskAversion, Survey } from 'Containers/ProfilePage/types';
import { InsuranceType } from 'Containers/ViewBenefitPage/types';
import { ContentfulTextNoteType, selectAlexGoNotes } from 'ContentfulDefaults/types/_shared';
import { ContentfulFbsBenefit } from 'ContentfulDefaults/types/benefits';
import { ContentfulSupplementalPlan } from 'ContentfulDefaults/types/benefitsAdditional';
import { ContentfulInfoBar } from 'ContentfulWrappers/ContentfulInfoBar';
import { ContentfulInfoBars } from 'ContentfulWrappers/ContentfulInfoBars';
import { ContentfulTextNote } from 'ContentfulWrappers/ContentfulTextNote';
import { ContentfulHeaderWrapper } from 'ContentfulWrappers/header';
import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useTextContext } from 'Contexts/textContext';
import { FadeUp, H4, Spinner, Tooltip } from 'DesignLibrary/atoms';
import { PageLayout } from 'DesignLibrary/atoms/PageLayout';
import InfoBar from 'DesignLibrary/molecules/InfoBar';
import Text from 'Shared/Text';
import {
  CoveredMembers,
  ReasonsWhy,
  Recommendation,
  SelectedPlansCoveredMembers,
  SelectedSupplementalPlans,
  SupplementalBenefitGroup,
  SupplementalPlan,
  SupplementalPlansFullHousehold,
  SupplementalRecommendations,
} from 'Types/entities';
import { BuilderGoSettings, CdnModuleConfig, CdnPlanInfo } from 'Utils/apiTypes';
import { getIsRichTextEmpty } from 'Utils/helpers';
import injectSaga from 'Utils/injectSaga';
import { sendInsightsEvent } from 'Utils/insights';
import Logger from 'Utils/logger';
import { VIEW_BENEFIT_PATH, OVERVIEW_PATH } from 'Utils/urls';

import { getSupplementalPlans } from './actions';
import { getNonSelectableBenefits, getSelectableBenefits } from './constants';
import saga from './saga';
import {
  makeGetIsHealthPlanSelected,
  makeGetIsLimitedPurposeFsa,
  makeGetSelectedHealthDeductible,
  getRiskAversion,
  makeSelectOverviewField,
  makeGetSupplementalPlanRecommendations,
  makeGetSupplementalPlanReasonsWhy,
} from './selectors';
import { SecondaryHeader, GroupSection, FbsGrid, SpinnerLayout, NoBenefits } from './styled';
import SupplementalCard from './SupplementalCard';

interface OverviewPageStateProps {
  client: string;
  employerName: string;
  fbsBenefitGroups: SupplementalBenefitGroup[];
  selectedSupplementalPlans: SelectedSupplementalPlans;
  supplementalPlans: SupplementalPlansFullHousehold;
  supplementalRecommendations: SupplementalRecommendations;
  reasonsWhy: ReasonsWhy;
  isLimitedPurposeFsa: boolean;
  isHealthPlanSelected: boolean;
  selectedHealthPlan: Recommendation | Record<string, never>;
  isLoading: boolean;
  planCoveredMembers: SelectedPlansCoveredMembers;
  isFirstPageView: boolean;
  coveredHouseholdMemberCount: number;
  showEnrolleeSelectDisclaimer: boolean;
  selectedHealthDeductible: number;
  dependentsCount: number;
  surveyAnswers: Survey;
  riskAversion: RiskAversion;
  builderCustomerKey: string | null;
  builderGoSettings: BuilderGoSettings | null;
  dataRevisionLocation: string | null;
  planYear?: number;
}

interface OverviewPageDispatchProps {
  fetchSupplementalPlans: () => void;
}

export type OverviewPageProps = OverviewPageStateProps & OverviewPageDispatchProps;

export const OverviewPage = ({
  client,
  employerName,
  fbsBenefitGroups,
  selectedSupplementalPlans,
  supplementalPlans,
  supplementalRecommendations,
  reasonsWhy,
  isLimitedPurposeFsa,
  isHealthPlanSelected,
  selectedHealthPlan,
  isLoading,
  planCoveredMembers,
  isFirstPageView,
  coveredHouseholdMemberCount,
  showEnrolleeSelectDisclaimer,
  selectedHealthDeductible,
  dependentsCount,
  surveyAnswers,
  riskAversion,
  builderCustomerKey,
  builderGoSettings,
  dataRevisionLocation,
  planYear,
  fetchSupplementalPlans,
}: OverviewPageProps) => {
  const { retrieveContentfulData, locale } = useTextContext();
  // Builder's CDN plan details are English-only, so any non-English locale uses Contentful instead.
  const isNonEnglish = locale !== 'en-US';
  const navigate = useNavigate();

  useEffect(() => {
    // Load supplemental plans
    fetchSupplementalPlans();
  }, []);

  const [suppRecsState, setSuppRecsState] = useState<SupplementalRecommendations>();
  useEffect(() => {
    if (!_.isEmpty(supplementalRecommendations) && !suppRecsState) {
      sendInsightsEvent(null, 'fbs_recommendations', {
        fbs_recommendations: supplementalRecommendations,
      });
      setSuppRecsState(supplementalRecommendations);
    }
  }, [supplementalRecommendations]);

  // All data parsed from CDN config.json for custom_module_* tiles
  const [cdnFetchFailed, setCdnFetchFailed] = useState(false);
  const [cdnFetchLoading, setCdnFetchLoading] = useState(!!dataRevisionLocation);
  const [cdnModuleState, setCdnModuleState] = useState({
    externalIdToModuleKey: {} as Record<string, string>,
    moduleKeyToTitle: {} as Record<string, string>,
    externalIdToCdnPlan: {} as Record<string, CdnPlanInfo>,
    allCdnExternalIds: new Set<string>(),
  });
  const { externalIdToModuleKey, moduleKeyToTitle, externalIdToCdnPlan, allCdnExternalIds } = cdnModuleState;

  useEffect(() => {
    const controller = new AbortController();
    if (dataRevisionLocation) {
      setCdnFetchFailed(false);
      setCdnFetchLoading(true);
      setCdnModuleState({
        externalIdToModuleKey: {},
        moduleKeyToTitle: {},
        externalIdToCdnPlan: {},
        allCdnExternalIds: new Set(),
      });
      fetch(dataRevisionLocation, { signal: controller.signal })
        .then((res) => (res.ok ? res.json() : undefined))
        .then((config: Record<string, CdnModuleConfig> | undefined) => {
          const externalIdToModuleKeyMap: Record<string, string> = {};
          const moduleKeyToTitleMap: Record<string, string> = {};
          const externalIdToCdnPlanMap: Record<string, CdnPlanInfo> = {};
          const allCdnExternalIdsSet = new Set<string>();
          if (config) {
            Object.entries(config).forEach(([key, module]) => {
              if (!key.startsWith('custom_module_')) return;
              (module.plan ?? []).forEach((plan) => allCdnExternalIdsSet.add(String(plan.id)));
              if (module?.settings?.has_details === true) {
                if (module.title) moduleKeyToTitleMap[key] = module.title;
                (module.plan ?? []).forEach((plan) => {
                  const externalId = String(plan.id);
                  externalIdToModuleKeyMap[externalId] = key;
                  externalIdToCdnPlanMap[externalId] = {
                    nickname: plan.nickname ?? '',
                    details: plan.details ?? '',
                  };
                });
              }
            });
          }
          setCdnModuleState({
            externalIdToModuleKey: externalIdToModuleKeyMap,
            moduleKeyToTitle: moduleKeyToTitleMap,
            externalIdToCdnPlan: externalIdToCdnPlanMap,
            allCdnExternalIds: allCdnExternalIdsSet,
          });
          setCdnFetchLoading(false);
        })
        .catch((err) => {
          if (err.name !== 'AbortError') {
            Logger.error('[OverviewPage] Failed to fetch CDN config', {
              url: dataRevisionLocation,
              error: err.message,
            });
            setCdnFetchFailed(true);
            setCdnFetchLoading(false);
          }
        });
    }
    return () => {
      controller.abort();
    };
  }, [dataRevisionLocation]);

  const moduleKeyToCdnPlanDetails = useMemo(() => {
    const result: Record<string, Record<string, CdnPlanInfo>> = {};
    Object.entries(externalIdToModuleKey).forEach(([externalId, moduleKey]) => {
      const planInfo = externalIdToCdnPlan[externalId];
      if (planInfo) {
        result[moduleKey] = { ...(result[moduleKey] ?? {}), [externalId]: planInfo };
      }
    });
    return result;
  }, [externalIdToModuleKey, externalIdToCdnPlan]);

  const contentfulSupplementalPlansArray = retrieveContentfulData<ContentfulSupplementalPlan[]>(
    'benefits_section_additional.supplemental_plans',
    [],
  );

  const contentfulSupplementalPlans: Record<string, ContentfulSupplementalPlan> = useMemo(
    () =>
      contentfulSupplementalPlansArray.reduce(
        (acc, i) => ({
          [i.plan_id]: {
            ...i,
          },
          ...acc,
        }),
        {},
      ),
    [contentfulSupplementalPlansArray],
  );

  const customModuleGroups = useMemo(() => {
    const otherPlans = (supplementalPlans?.Other?.plans ?? []) as SupplementalPlan[];
    const groups = new Map<string, SupplementalPlan[]>();
    otherPlans.forEach((plan) => {
      const moduleKey = externalIdToModuleKey[plan.external_id];
      if (!moduleKey) return;
      // Nothing to open unless content exists somewhere — mirrors BenefitDetails.renderDescription's
      // own fallback chain (CDN details -> Contentful entry -> API benefits.description). CDN details
      // is zeroed out for non-English locales at render time (see renderCustomModuleTiles below), so it
      // can't count as content here either, or the tile would pass this check and still render blank.
      // Deliberately stricter than BenefitDetails here: that component renders an empty paragraph for
      // a Contentful entry with no description, we require the description to actually have text — a
      // touched-but-never-filled-in rich text field comes through as a real object with empty content
      // (e.g. { nodeType: 'document', content: [] }), so a bare truthiness check isn't enough.
      const hasCdnDetails =
        !isNonEnglish && (externalIdToCdnPlan[plan.external_id]?.details ?? '').trim() !== '';
      const contentfulDescription = contentfulSupplementalPlans[plan.external_id]?.description;
      const hasContentfulDescription = !!contentfulDescription && !getIsRichTextEmpty(contentfulDescription);
      const hasApiDescription = !_.isEmpty(plan.benefits?.description);
      if (!hasCdnDetails && !hasContentfulDescription && !hasApiDescription) return;
      groups.set(moduleKey, [...(groups.get(moduleKey) ?? []), plan]);
    });
    return groups;
  }, [
    supplementalPlans?.Other?.plans,
    externalIdToModuleKey,
    externalIdToCdnPlan,
    contentfulSupplementalPlans,
    isNonEnglish,
  ]);

  const handleGoToBenefit = (insuranceType: InsuranceType) => {
    navigate(`${VIEW_BENEFIT_PATH}/${insuranceType}`);
  };

  const educationalBenefits = retrieveContentfulData<ContentfulFbsBenefit[]>(
    'benefits_section.educational_benefits',
    [],
  );

  const supplementalAlexGoNotes = retrieveContentfulData<ContentfulTextNoteType[]>(
    'benefits_section_additional.alex_go_supplemental_note',
  );

  const selectedSupplementalAlexGoNotes = selectAlexGoNotes(
    supplementalAlexGoNotes,
    { planYear, upcomingPlanYear: undefined },
    false,
  );

  const getEducationalBenefitTypes = () => educationalBenefits.map((benefit) => benefit.plan_type);

  const healthHsaEligible = isHealthPlanSelected && selectedHealthPlan.plan.hsa_eligible;
  const { is_cancer_insurance_enabled, is_life_ltc_enabled } = useFeatureFlagContext();

  const planCount = Object.entries(supplementalPlans).filter(([key]) =>
    getSelectableBenefits(client).includes(key as InsuranceType),
  ).length;

  const otherCount = Object.entries(supplementalPlans).filter(
    ([key]) =>
      getNonSelectableBenefits(client).includes(key as InsuranceType) && !['hsa', 'hra'].includes(key),
  ).length;

  let selectableBenefits = getSelectableBenefits(client).filter(
    (insuranceType) => insuranceType in supplementalPlans,
  );
  if (!is_cancer_insurance_enabled) {
    selectableBenefits = selectableBenefits.filter((x) => x !== 'cancer');
  }
  // Feature flag to check if we have to include "Life + LTC" or not
  if (!is_life_ltc_enabled) {
    selectableBenefits = selectableBenefits.filter((x) => x !== 'ltc');
  }
  const totalCount = planCount + otherCount;

  // Given a group, return true if it contains at least one eligible benefit
  const hasEligibleBenefitsinGroup = (groupBenefits) => {
    const eligiblePlanTypes = [...getEducationalBenefitTypes(), ...Object.keys(supplementalPlans)];

    // Iterate group benefits, if benefit is included in eligible benefits return true,
    // If benefit is hsa, only return true if selectedHealthPlan is hsa_eligible
    return groupBenefits.plan_types.some(
      ({ plan_type }) => eligiblePlanTypes.includes(plan_type) && (plan_type !== 'hsa' || healthHsaEligible),
    );
  };

  const fbsBestMatch = Object.values(supplementalRecommendations).some((i) => i.label === 'best');

  const renderGroups = () => (
    <>
      {/* TICKETING THIS TO BE CLEANED UP LATER. THESE ITEMS SHOULD BE REMOVED FROM DJANGO clientconfig TABLE */}
      {fbsBenefitGroups?.map((g) => {
        if (hasEligibleBenefitsinGroup(g)) {
          const group = {
            ...g,
            plan_types:
              g.title === 'Tax-Free Accounts'
                ? g.plan_types.filter(
                    (item) =>
                      item.plan_type !== 'hsa' &&
                      item.plan_type !== 'hra' &&
                      // Possible typo 'fsa_medical' - ask @jakeblank what's going on here
                      item.plan_type !== 'fsa-medical',
                  )
                : g.plan_types,
          };
          return (
            <GroupSection key={group.title} data-testid={`group-${group.title}`}>
              <SecondaryHeader className="header">
                <H4 as="h2" weight="normal">
                  {group.title}
                </H4>
                {group.description && <Tooltip id={group.title} label={group.description} />}
              </SecondaryHeader>

              <FbsGrid>
                {group.plan_types
                  .sort((a, b) => a.position - b.position)
                  .map((item) => {
                    const insuranceType = item.plan_type as InsuranceType;

                    if (!Object.keys(supplementalPlans).includes(insuranceType)) {
                      if (!getEducationalBenefitTypes().includes(insuranceType)) return null;
                    }

                    if (
                      getSelectableBenefits(client).includes(insuranceType) &&
                      supplementalPlans[insuranceType]
                    ) {
                      return (
                        <SupplementalCard
                          key={insuranceType}
                          client={client}
                          employerName={employerName}
                          insuranceType={insuranceType}
                          contentfulSupplementalPlans={contentfulSupplementalPlans}
                          supplementalPlans={supplementalPlans[insuranceType]?.plans as SupplementalPlan[]}
                          selectedPlan={selectedSupplementalPlans[insuranceType] as SupplementalPlan}
                          coveredMembers={planCoveredMembers[insuranceType] as CoveredMembers}
                          handleOpenBenefit={handleGoToBenefit}
                          recommendedLabel={supplementalRecommendations[insuranceType]?.label}
                          isLimitedPurposeFsa={isLimitedPurposeFsa}
                          reasonsWhy={reasonsWhy}
                          selectedHealthDeductible={selectedHealthDeductible}
                          dependentsCount={dependentsCount}
                          surveyAnswers={surveyAnswers}
                          riskAversion={riskAversion}
                          fbsBestMatch={fbsBestMatch}
                          builderCustomerKey={builderCustomerKey}
                          builderGoSettings={builderGoSettings}
                        />
                      );
                    }

                    if (
                      getNonSelectableBenefits(client).includes(insuranceType) &&
                      !getEducationalBenefitTypes().includes(insuranceType)
                    ) {
                      let plans = supplementalPlans[insuranceType]?.plans as SupplementalPlan[];
                      if (insuranceType === 'Other') {
                        if (cdnFetchFailed || cdnFetchLoading) return null;
                        plans = plans?.filter((p) => !allCdnExternalIds.has(p.external_id));
                        if (!plans?.length) return null;
                      }
                      return (
                        <SupplementalCard
                          key={insuranceType}
                          client={client}
                          employerName={employerName}
                          insuranceType={insuranceType}
                          supplementalPlans={plans}
                          contentfulSupplementalPlans={contentfulSupplementalPlans}
                          handleOpenBenefit={handleGoToBenefit}
                          // Plans like EAP can be auto-enrolled
                          // need to pass selectedPlan so this displays on FBS page as auto-enrolled
                          selectedPlan={
                            selectedSupplementalPlans[insuranceType]
                              ? (selectedSupplementalPlans[insuranceType] as SupplementalPlan)
                              : null
                          }
                          isLimitedPurposeFsa={isLimitedPurposeFsa}
                          recommendedLabel={supplementalRecommendations[insuranceType]?.label}
                          reasonsWhy={reasonsWhy}
                          selectedHealthDeductible={selectedHealthDeductible}
                          dependentsCount={dependentsCount}
                          surveyAnswers={surveyAnswers}
                          riskAversion={riskAversion}
                          fbsBestMatch={fbsBestMatch}
                          builderCustomerKey={builderCustomerKey}
                          builderGoSettings={builderGoSettings}
                        />
                      );
                    }
                    if (getEducationalBenefitTypes().includes(insuranceType)) {
                      return (
                        <SupplementalCard
                          contentfulSupplementalPlans={contentfulSupplementalPlans}
                          selectedPlan={
                            selectedSupplementalPlans[insuranceType]
                              ? (selectedSupplementalPlans[insuranceType] as SupplementalPlan)
                              : null
                          }
                          key={insuranceType}
                          client={client}
                          employerName={employerName}
                          insuranceType={insuranceType}
                          isEducational
                          handleOpenBenefit={handleGoToBenefit}
                          isLimitedPurposeFsa={isLimitedPurposeFsa}
                          recommendedLabel={supplementalRecommendations[insuranceType]?.label}
                          reasonsWhy={reasonsWhy}
                          selectedHealthDeductible={selectedHealthDeductible}
                          dependentsCount={dependentsCount}
                          surveyAnswers={surveyAnswers}
                          riskAversion={riskAversion}
                          contentfulPlan={
                            educationalBenefits.find(
                              (i) => i.plan_type === insuranceType,
                            ) as ContentfulFbsBenefit
                          }
                          builderCustomerKey={builderCustomerKey}
                          builderGoSettings={builderGoSettings}
                        />
                      );
                    }

                    return null;
                  })}
              </FbsGrid>
            </GroupSection>
          );
        }
        return null;
      })}
    </>
  );

  const renderSupplementalPlans = () =>
    selectableBenefits.map((insuranceType) => (
      <SupplementalCard
        key={insuranceType}
        client={client}
        employerName={employerName}
        insuranceType={insuranceType}
        contentfulSupplementalPlans={contentfulSupplementalPlans}
        supplementalPlans={supplementalPlans[insuranceType]?.plans as SupplementalPlan[]}
        selectedPlan={selectedSupplementalPlans[insuranceType] as SupplementalPlan}
        coveredMembers={planCoveredMembers[insuranceType] as CoveredMembers}
        handleOpenBenefit={handleGoToBenefit}
        recommendedLabel={supplementalRecommendations[insuranceType]?.label}
        isLimitedPurposeFsa={isLimitedPurposeFsa}
        reasonsWhy={reasonsWhy}
        selectedHealthDeductible={selectedHealthDeductible}
        dependentsCount={dependentsCount}
        surveyAnswers={surveyAnswers}
        riskAversion={riskAversion}
        fbsBestMatch={fbsBestMatch}
        builderCustomerKey={builderCustomerKey}
        builderGoSettings={builderGoSettings}
      />
    ));

  const renderOtherPlans = () =>
    getNonSelectableBenefits(client).map((insuranceType) => {
      // check if insurance type exists in supplemental plan json
      if (insuranceType in supplementalPlans && !getEducationalBenefitTypes().includes(insuranceType)) {
        let plans = supplementalPlans[insuranceType]?.plans as SupplementalPlan[];
        if (insuranceType === 'Other') {
          if (cdnFetchFailed || cdnFetchLoading) return null;
          plans = plans?.filter((p) => !allCdnExternalIds.has(p.external_id));
          if (!plans?.length) return null;
        }
        return (
          <SupplementalCard
            key={insuranceType}
            client={client}
            employerName={employerName}
            insuranceType={insuranceType}
            contentfulSupplementalPlans={contentfulSupplementalPlans}
            supplementalPlans={plans}
            handleOpenBenefit={handleGoToBenefit}
            // Plans like EAP can be auto-enrolled
            // need to pass selectedPlan so this displays on FBS page as auto-enrolled
            selectedPlan={
              selectedSupplementalPlans[insuranceType]
                ? (selectedSupplementalPlans[insuranceType] as SupplementalPlan)
                : null
            }
            recommendedLabel={supplementalRecommendations[insuranceType]?.label}
            isLimitedPurposeFsa={isLimitedPurposeFsa}
            reasonsWhy={reasonsWhy}
            selectedHealthDeductible={selectedHealthDeductible}
            dependentsCount={dependentsCount}
            surveyAnswers={surveyAnswers}
            riskAversion={riskAversion}
            fbsBestMatch={fbsBestMatch}
            builderCustomerKey={builderCustomerKey}
            builderGoSettings={builderGoSettings}
          />
        );
      }

      return null;
    });

  const renderCustomModuleTiles = () => {
    // CDN fetch loading or failed: render each Other plan individually using plan.name as the title
    // so tiles remain visible without flashing the generic "Other Benefits" type label.
    if (cdnFetchFailed || cdnFetchLoading) {
      const otherPlans = (supplementalPlans?.Other?.plans ?? []) as SupplementalPlan[];
      return otherPlans.map((plan) => {
        // Use a per-plan synthetic key so each tile gets a unique data-testid
        const fallbackType = `custom_module_${plan.external_id}` as InsuranceType;
        return (
          <SupplementalCard
            key={plan.external_id}
            client={client}
            employerName={employerName}
            insuranceType={fallbackType}
            contentfulSupplementalPlans={contentfulSupplementalPlans}
            supplementalPlans={[plan]}
            handleOpenBenefit={handleGoToBenefit}
            selectedPlan={null}
            isLimitedPurposeFsa={isLimitedPurposeFsa}
            reasonsWhy={reasonsWhy}
            selectedHealthDeductible={selectedHealthDeductible}
            dependentsCount={dependentsCount}
            surveyAnswers={surveyAnswers}
            riskAversion={riskAversion}
            fbsBestMatch={fbsBestMatch}
            builderCustomerKey={builderCustomerKey}
            builderGoSettings={builderGoSettings}
            overrideTitle={plan.name}
          />
        );
      });
    }

    // custom_module_* plans are text-only; selectedPlan is intentionally always null
    // Always pass the CDN title as overrideTitle so it serves as an English fallback when
    // Contentful has no translated plan_title. SupplementalCard prefers Contentful over CDN.
    // Zero out CDN details in non-English locales so BenefitDetails falls through to the Contentful
    // locale-aware description instead of showing English CDN text.
    return Array.from(customModuleGroups.entries()).map(([moduleKey, plansForModule]) => {
      // custom_module_* keys are not valid InsuranceType values but are safe here:
      // they won't match any CONTENT_MAP entry, falling through to the correct paths.
      const insuranceType = moduleKey as InsuranceType;
      const resolvedTitle = moduleKeyToTitle[moduleKey];
      const rawCdnPlanDetails = moduleKeyToCdnPlanDetails[moduleKey];
      const resolvedCdnPlanDetails =
        isNonEnglish && rawCdnPlanDetails
          ? Object.fromEntries(
              Object.entries(rawCdnPlanDetails).map(([id, info]) => [id, { ...info, details: '' }]),
            )
          : rawCdnPlanDetails;
      return (
        <SupplementalCard
          key={insuranceType}
          client={client}
          employerName={employerName}
          insuranceType={insuranceType}
          contentfulSupplementalPlans={contentfulSupplementalPlans}
          supplementalPlans={plansForModule}
          handleOpenBenefit={handleGoToBenefit}
          selectedPlan={null}
          recommendedLabel={supplementalRecommendations[insuranceType]?.label}
          isLimitedPurposeFsa={isLimitedPurposeFsa}
          reasonsWhy={reasonsWhy}
          selectedHealthDeductible={selectedHealthDeductible}
          dependentsCount={dependentsCount}
          surveyAnswers={surveyAnswers}
          riskAversion={riskAversion}
          fbsBestMatch={fbsBestMatch}
          builderCustomerKey={builderCustomerKey}
          builderGoSettings={builderGoSettings}
          overrideTitle={resolvedTitle}
          cdnPlanDetails={resolvedCdnPlanDetails}
        />
      );
    });
  };

  const renderEducationalPlans = () =>
    educationalBenefits.map((benefit) => {
      const insuranceType = benefit.plan_type as InsuranceType;
      return (
        <SupplementalCard
          key={insuranceType}
          client={client}
          employerName={employerName}
          insuranceType={insuranceType}
          contentfulPlan={benefit}
          handleOpenBenefit={handleGoToBenefit}
          isLimitedPurposeFsa={isLimitedPurposeFsa}
          recommendedLabel={supplementalRecommendations[insuranceType]?.label}
          reasonsWhy={reasonsWhy}
          selectedHealthDeductible={selectedHealthDeductible}
          dependentsCount={dependentsCount}
          surveyAnswers={surveyAnswers}
          riskAversion={riskAversion}
          builderCustomerKey={builderCustomerKey}
          isEducational
          builderGoSettings={builderGoSettings}
        />
      );
    });

  const renderEnrolleeInfoBar = () => {
    if (showEnrolleeSelectDisclaimer && coveredHouseholdMemberCount > 1 && selectableBenefits.length > 0) {
      return (
        <ContentfulInfoBar
          field="benefits_section_additional.enrollee_select_disclaimer"
          key="benefits_section_additional.enrollee_select_disclaimer"
        />
      );
    }
    return undefined;
  };

  const customModuleTiles = renderCustomModuleTiles();

  return (
    <>
      <ContentfulHeaderWrapper sectionKey="benefits_section_additional" animate={isFirstPageView} />
      <ContentfulInfoBars sectionKey="benefits_section_additional">
        <>
          {selectedSupplementalAlexGoNotes.map((note) => (
            <ContentfulTextNote key={note.plan_year} note={note} />
          ))}
          {renderEnrolleeInfoBar()}
        </>
      </ContentfulInfoBars>
      <PageLayout page="overview">
        <FadeUp isOpen={!isLoading}>
          {totalCount > 0 ? (
            fbsBenefitGroups?.length > 0 ? (
              <>
                {renderGroups()}
                {customModuleTiles.length > 0 && <FbsGrid>{customModuleTiles}</FbsGrid>}
              </>
            ) : (
              <FbsGrid>
                {renderSupplementalPlans()}
                {renderOtherPlans()}
                {customModuleTiles}
                {renderEducationalPlans()}
              </FbsGrid>
            )
          ) : (
            <NoBenefits>
              <InfoBar icon="WarningCircle" text={<Text field="benefits_section.no_benefits_available" />} />
            </NoBenefits>
          )}
        </FadeUp>
        <FadeUp isOpen={isLoading}>
          <SpinnerLayout>
            <Spinner color="--input-gray" />
          </SpinnerLayout>
        </FadeUp>
      </PageLayout>
    </>
  );
};

const mapStateToProps = createStructuredSelector<GlobalReducerState, OverviewPageStateProps>({
  client: makeSelectConfigField('client'),
  employerName: makeSelectJvpField('employerName'),
  supplementalPlans: makeSelectOverviewField('supplemental_plans_full_household'),
  supplementalRecommendations: makeGetSupplementalPlanRecommendations(),
  planCoveredMembers: makeSelectOverviewField('selected_plans_covered_members'),
  isHealthPlanSelected: makeGetIsHealthPlanSelected(),
  isLoading: makeSelectOverviewField('isLoading'),
  selectedSupplementalPlans: makeSelectOverviewField('selected_plans'),
  selectedHealthPlan: makeSelectCommercialField('selectedHealthPlan'),
  fbsBenefitGroups: makeSelectConfigField('fbs_groups'),
  isFirstPageView: selectIsFirstPageView(OVERVIEW_PATH),
  coveredHouseholdMemberCount: makeGetCoveredHouseholdMemberCount(),
  showEnrolleeSelectDisclaimer: makeSelectConfigField('show_enrollee_select_disclaimer'),
  reasonsWhy: makeGetSupplementalPlanReasonsWhy(),
  isLimitedPurposeFsa: makeGetIsLimitedPurposeFsa(),
  selectedHealthDeductible: makeGetSelectedHealthDeductible(),
  dependentsCount: makeSelectDependentsCount(),
  surveyAnswers: makeSelectSurvey(),
  riskAversion: getRiskAversion(),
  builderCustomerKey: makeSelectConfigField('builder_customer_key'),
  builderGoSettings: makeGetBuilderGoSettings(),
  dataRevisionLocation: makeGetDataRevisionLocation(),
  planYear: makeGetSelectedPublicationPlanYear(),
});

export function mapDispatchToProps(dispatch): OverviewPageDispatchProps {
  return {
    fetchSupplementalPlans: () => dispatch(getSupplementalPlans()),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withSaga = injectSaga({
  key: 'overviewPage',
  saga,
});

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
export default compose(withSaga, withConnect)(OverviewPage);
